import { Injectable } from '@angular/core';
import { DashabordChartSummary } from '../models/dashboardChartSummary';
import { Observable } from 'rxjs';
import { AssetHierarchy } from '../models/assetHierarchy';
import { DashboardAlertData } from '../models/dashabordAlertData';
import { HttpClient } from '@angular/common/http';
import { ApiConstants } from '../constants/api-constant';
import { environment } from '../../../environments/environment';
import { AlertFilter } from '../models/alertFilter';
import { EventDetails } from '../models/eventDetails';
import { CameraConstants } from '../constants/camera-constant';
import { EventChart } from '../models/eventChart';
import { countryList } from '../models/dashabordEventDetails';
import { DashboardConstants } from '../constants/dashboard-constants';
import { WORKFLOW } from '../constants/camera-profile-constant';
import { Features } from '../models/feature';
import { GatewayStatusDetail } from '../models/gateway';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  constructor(private http: HttpClient) {}

  public getDashabordSummaryData(
    country: string,
    startTime: string,
    endTime: string,
    showAllEvents: string
  ): Observable<DashabordChartSummary> {
    return this.http.get<DashabordChartSummary>(
      `${environment.apiBaseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}get-summary-data?countryName=${country}
&startDate=${startTime}&endDate=${endTime}&showAllEvents=${showAllEvents}`
    );
  }

  public getAssetHirarchy(country: string): Observable<AssetHierarchy> {
    return this.http.get<AssetHierarchy>(
      `${environment.apiBaseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}get-asset-hierarchy?countryName=${country}`
    );
  }

  public getGatewayDetailByAssetId(assetId: string): Observable<GatewayStatusDetail> {
    return this.http.get<GatewayStatusDetail>(
      `${environment.apiBaseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}status-details/${assetId}`
    );
  }

  public getAlertData(countryName: string, alertFilter: AlertFilter, showAllEvents: string): Observable<DashboardAlertData> {
    return this.http.get<DashboardAlertData>(
      `${environment.apiBaseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}get-alert-data?countryName=${countryName}
&rigId=${alertFilter.rigId}&workflow=${alertFilter.workflow}&gatewayStatus=${alertFilter.gatewayStatus}
&cameraStatus=${alertFilter.cameraStatus}&showAllEvents=${showAllEvents}
&pageNo=${alertFilter.pageNumber}&records=${alertFilter.recordsPerPage}`
    );
  }

  public getDashboardEventTrendingData(
    country: string,
    startTime: string,
    endTime: string,
    timezone: string,
    showAllEvents: string
  ): Observable<EventChart> {
    return this.http.get<EventChart>(
      `${environment.apiBaseUrl}${ApiConstants.BASEURL}${ApiConstants.EVENT}get-event-trend?gatewayId=&startDate=${startTime}
&endDate=${endTime}&countryName=${country}&controllerId=&timeZone=${timezone}&showAllEvents=${showAllEvents}`
    );
  }

  public getCountryData(): Observable<countryList> {
    return this.http.get<countryList>(`${environment.apiBaseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}get-country-list`);
  }

  public getFeature(): Observable<Features[]> {
    return this.http.get<Features[]>(`${environment.apiBaseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}feature`);
  }

  public getRigState(value: number): string {
    switch (value) {
      case 0:
        return 'Wits';
      case 1:
        return 'Gn5';
      default:
        return 'NA';
    }
  }

  public getRigEventDetails(event: EventDetails): string {
    let eventInfo = '';
    if (event.rigState?.toLowerCase() === CameraConstants.REDZONESTATIC) {
      eventInfo = CameraConstants.NoRigData;
    } else {
      if (event.workflowType === DashboardConstants.PPE_WORKFLOW || event.workflowType === DashboardConstants.PIPERACK_WORKFLOW) {
        const comment = event.rigState.toString().replace(/[&\/\\#+()$~%.'":*?<>{}]/g, '');
        const missingEvents = comment.split(',');
        missingEvents.sort((a, b) => (a.replace(/\s/g, '') > b.replace(/\s/g, '') ? 1 : -1));
        if (missingEvents.length > 0) {
          missingEvents.map((e: string, index: number) => {
            let data = '';
            if (event.workflowType === DashboardConstants.PPE_WORKFLOW) {
              data = this.getMissingPpe(e.replace(/\s/g, ''));
            } else {
              data = this.formatLabel(e);
            }
            if (data !== '') {
              eventInfo = eventInfo + data;
              if (index < missingEvents.length - 1) {
                eventInfo = eventInfo + ', ';
              }
            }
          });
        } else {
          eventInfo = this.getMissingPpe(event.comment.replace(/\s/g, ''));
        }
      } else {
        if (event?.rigState && event.rigState !== 'NA') {
          eventInfo = this.formatLabel(event.rigState);
        } else {
          eventInfo = CameraConstants.NoRigData;
        }
      }
    }

    return eventInfo;
  }

  public downloadZipFile(url: string, controllerId: string): void {
    const blob = new Blob([url], {
      type: 'application/zip',
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const filename = 'SLB Edge-Vision Intelligence-' + controllerId;
    const dwldLink = document.createElement('a');

    dwldLink.setAttribute('href', blobUrl);
    dwldLink.setAttribute('download', filename + '.zip');
    dwldLink.setAttribute('target', '_blank');
    dwldLink.click();
    dwldLink.remove();
  }

  public unsubscribeEmail(user_email: string, org_id: number): Observable<string> {
    return this.http.get<string>(
      `${environment.apiBaseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}unsusbscribe-email/${user_email}/${org_id}`
    );
  }

  public formatLabel(key: string): string {
    if (key === DashboardConstants.OFF) {
      return key;
    } else {
      key.replace(key[0], key[0].toUpperCase());
      const splitUpperCase = key.replace(key[0], key[0].toUpperCase()).match(/[A-Z][a-z]+/g);
      if (splitUpperCase?.length) {
        return splitUpperCase.join(' ');
      }

      return '';
    }
  }

  public getIconImg(eventDetails: EventDetails): string {
    let icon = '';
    if (eventDetails?.workflowType) {
      icon = this.getIcon(eventDetails?.workflowType);
    }

    return icon;
  }

  public getIcon(workflow: string): string {
    let icon = '';
    if (workflow) {
      icon = WORKFLOW.filter(data => data.value?.toLowerCase() === workflow?.toLowerCase())?.[0]?.icon;
    } else {
      icon = DashboardConstants.REDZONE_ICON;
    }

    return icon;
  }

  public getReason(eventDetails: EventDetails): string {
    let workflowType = '';
    if (eventDetails?.workflowType && !eventDetails?.rigState?.toLowerCase().includes('crossing') && eventDetails?.rigState !== null) {
      workflowType = WORKFLOW.filter(data => data.value?.toLowerCase() === eventDetails.workflowType.toLowerCase())?.[0]?.redZoneLabel;
    }

    return workflowType;
  }

  public getWorkFlowType(eventDetails: EventDetails): string {
    let workflowType = '';
    if (eventDetails?.workflowType) {
      workflowType = this.setWorkflowLabel(eventDetails?.workflowType);
    }

    return workflowType;
  }

  public setWorkflowLabel(key: string): string {
    let value = '';
    if (key) {
      value = WORKFLOW.filter(data => data.value?.toLowerCase() === key?.toLowerCase())?.[0]?.label;
    }

    return value;
  }

  public userLogout(): Observable<void> {
    return this.http.post<void>(`${environment.apiBaseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}signout`, {});
  }

  private getMissingPpe(value: string): string {
    switch (value?.toUpperCase()) {
      case 'HELMET':
        return 'Hard Hat';
      case 'COVERALL':
        return 'Coverall';
      case 'GLOVE':
        return 'Gloves';
      case 'VEST':
        return 'High Visibility Vest';
      case 'GOGGLES':
        return 'Safety Glasses';
      case 'PERSONWITHOUTOXYGENTANK':
        return 'Self Contained Breathing Apparatus';
      default:
        return '';
    }
  }
}
